import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const PanduanDoaPage = () => {
  const navigate = useNavigate();
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  
  // Check student access
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const userRole = localStorage.getItem('userRole');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (userData && isAuthenticated === 'true') {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if user is student
      if (parsedUser.role !== 'student' && userRole !== 'student') {
        navigate('/');
        return;
      }
    } else {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch prayers from API
  const fetchPrayers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const response = await authFetch(`${apiBase}/api/student/prayers`);
      
      if (!response.ok) {
        throw new Error('Gagal memuat data doa');
      }
      
      const result = await response.json();
      // Handle both paginated and direct array responses
      const prayersData = result.data || result || [];
      
      setPrayers(prayersData);
    } catch (err) {
      console.error('Error fetching prayers:', err);
      setError(err.message || 'Terjadi kesalahan saat memuat data doa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch prayers if user is authenticated as student
    const userRole = localStorage.getItem('userRole');
    
    if (user?.role === 'student' || userRole === 'student') {
      fetchPrayers();
    }
  }, [user]);
  
  // Helper function to capitalize category names
  const capitalizeCategory = (category) => {
    if (!category) return '';
    return category
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Grouping prayers by category
  const categories = {};
  prayers.forEach(prayer => {
    if (!categories[prayer.category]) {
      categories[prayer.category] = [];
    }
    categories[prayer.category].push(prayer);
  });
  
  const handlePrayerClick = (prayerId) => {
    if (expandedPrayer === prayerId) {
      setExpandedPrayer(null);
    } else {
      setExpandedPrayer(prayerId);
    }
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/pelayanan')}>
          <span>←</span> Kembali
        </BackButton>
        <h1>Panduan Doa Katolik</h1>
        <p>Kumpulan doa-doa untuk memperkuat iman dan spiritualitas</p>
      </PageHeader>
      
      <PrayerContainer>
        {loading ? (
          <LoadingState>
            <LoadingIcon>🙏</LoadingIcon>
            <LoadingText>Memuat doa-doa...</LoadingText>
          </LoadingState>
        ) : error ? (
          <ErrorState>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={fetchPrayers}>
              Coba Lagi
            </RetryButton>
          </ErrorState>
        ) : Object.keys(categories).length > 0 ? (
          Object.keys(categories).map(category => (
            <CategorySection key={category}>
              <CategoryTitle>{capitalizeCategory(category)}</CategoryTitle>
              <PrayerList>
                {categories[category].map(prayer => (
                  <PrayerItem key={prayer.id}>
                    <PrayerHeader onClick={() => handlePrayerClick(prayer.id)}>
                      <PrayerTitle>{prayer.name}</PrayerTitle>
                      <ExpandIcon expanded={expandedPrayer === prayer.id}>
                        {expandedPrayer === prayer.id ? '−' : '+'}
                      </ExpandIcon>
                    </PrayerHeader>
                    
                    {expandedPrayer === prayer.id && (
                      <PrayerContent>
                        {prayer.content.split('\n').map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                      </PrayerContent>
                    )}
                  </PrayerItem>
                ))}
              </PrayerList>
            </CategorySection>
          ))
        ) : (
          <EmptyState>
            <EmptyIcon>🙏</EmptyIcon>
            <EmptyText>Belum ada doa yang tersedia</EmptyText>
          </EmptyState>
        )}
      </PrayerContainer>
      
      <Footer>
        <QuoteBox>
          <QuoteIcon>"</QuoteIcon>
          <QuoteText>Doa itu bukan meminta apa yang kita inginkan, tetapi menerima apa yang Allah berikan.</QuoteText>
          <QuoteAuthor>— St. Teresa dari Avila</QuoteAuthor>
        </QuoteBox>
      </Footer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  position: relative;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
    text-align: center;
  }
  
  p {
    color: var(--text-light);
    text-align: center;
    font-size: 0.95rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  color: var(--primary);
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  span {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const PrayerContainer = styled.div`
  margin-bottom: 2rem;
`;

const CategorySection = styled.div`
  margin-bottom: 2rem;
`;

const CategoryTitle = styled.h2`
  font-size: 1.25rem;
  color: var(--primary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const PrayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PrayerItem = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const PrayerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  cursor: pointer;
`;

const PrayerTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-dark);
  margin: 0;
`;

const ExpandIcon = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.expanded ? 'var(--primary)' : 'var(--background-light)'};
  color: ${props => props.expanded ? 'white' : 'var(--text-dark)'};
  font-size: 1.2rem;
  line-height: 1;
  transition: all 0.2s;
`;

const PrayerContent = styled.div`
  padding: 0 1.25rem 1.25rem;
  color: var(--text-light);
  font-size: 0.95rem;
  line-height: 1.6;
  
  p {
    margin: 0.5rem 0;
  }
`;

const Footer = styled.div`
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
`;

const QuoteBox = styled.div`
  position: relative;
  background-color: rgba(43, 93, 161, 0.05);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
`;

const QuoteIcon = styled.span`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 4rem;
  color: var(--primary);
  opacity: 0.1;
  line-height: 1;
`;

const QuoteText = styled.p`
  font-size: 1.1rem;
  font-style: italic;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const QuoteAuthor = styled.p`
  font-size: 0.9rem;
  color: var(--text-light);
  font-weight: 500;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-radius: 12px;
  background-color: rgba(43, 93, 161, 0.05);
  margin-bottom: 2rem;
`;

const LoadingIcon = styled.span`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: var(--text-dark);
  font-weight: 500;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-radius: 12px;
  background-color: rgba(255, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const ErrorIcon = styled.span`
  font-size: 2rem;
  color: var(--error);
  margin-bottom: 1rem;
`;

const ErrorText = styled.p`
  font-size: 1rem;
  color: var(--text-dark);
  font-weight: 500;
  margin-bottom: 1rem;
`;

const RetryButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary);
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-radius: 12px;
  background-color: rgba(43, 93, 161, 0.05);
  margin-bottom: 2rem;
`;

const EmptyIcon = styled.span`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: var(--text-dark);
  font-weight: 500;
`;

export default PanduanDoaPage;